import { get as getAttr, map, omit, pick, reject } from "lodash-es";
import { ReactNode, useMemo } from "react";
import create from "zustand";

const generateId = () => Math.random().toString(36).substr(2, 9);

export type ToastType =
  | "primary"
  | "secondary"
  | "success"
  | "danger"
  | "warning"
  | "info";

export interface ToastProps {
  message: ReactNode;
  type?: ToastType;
  autoDismiss?: number;
  onClick?: () => void;
}

export interface Toast extends ToastProps {
  id: string;
  type: ToastType;
}

interface ToastState {
  activeToasts: string[];
  toastMap: Record<string, Toast>;
  toast: (newToast: ToastProps) => string;
  dismiss: (id: string) => void;
  clear: () => void;
  handleError: (e: unknown) => void;
}

export const useToastState = create<ToastState>((set, get) => ({
  activeToasts: [],
  toastMap: {},
  toast(toastProps: ToastProps): string {
    const id = generateId();
    const newToast: Toast = {
      type: "success",
      autoDismiss: 3000,
      ...toastProps,
      id,
    };
    set(({ toastMap, activeToasts }) => ({
      toastMap: { ...toastMap, [id]: { ...newToast } },
      activeToasts: [...activeToasts, id],
    }));
    setTimeout(() => get().dismiss(id), newToast.autoDismiss);
    return id;
  },
  dismiss(id) {
    set(({ toastMap, activeToasts }) => ({
      activeToasts: reject(activeToasts, (at) => at === id),
      toastMap: omit(toastMap, id),
    }));
  },
  clear() {
    set({ activeToasts: [], toastMap: {} });
  },
  handleError(e: unknown) {
    const { toast } = get();
    const res = getAttr(e, "response.data");
    if (res) {
      // TODO: Fix this type error
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      toast({ message: res.error, type: "danger" });
    } else {
      toast({ message: "An unknown error occurred.", type: "danger" });
    }
  },
}));

const toastDataSelector = (s: ToastState) =>
  pick(s, "activeToasts", "toastMap");
const dismissSelector = (s: ToastState) => s.dismiss;
const toastSelector = (s: ToastState) => s.toast;

export function useActiveToasts(): Toast[] {
  const { activeToasts, toastMap } = useToastState(toastDataSelector);
  return useMemo(
    () => map(activeToasts, (id) => toastMap[id]),
    [activeToasts, toastMap]
  );
}

export function useToastDismiss(): ToastState["dismiss"] {
  return useToastState(dismissSelector);
}

export function useToast(): ToastState["toast"] {
  return useToastState(toastSelector);
}
